import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactModal from 'react-modal';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { constants, verifiers } from '@yola/subscription-manager-js';
import { t } from '../localization';
import helpers from './helpers';
import viewMode from './constants/view-mode';
import discountType from './constants/discount-type';
import planFeaturesGroupIds from './constants/plan-features-gruop-ids';
import yoproductsServiceClient from '../common/helpers/yoproducts-service-client';
import DiscountBanner from './components/discount-banner';
import { API_TERM_VALUES, PER_MONTH, PER_YEAR } from '../common/constants/periods';
import usePlanFeatures from './hooks/use-plan-features';
import usePlansData from './hooks/use-plans-data';
import useCompactPlansData from './hooks/use-compact-plans-data';
import useAvailablePlans from './hooks/use-available-plans';
import useMatchBreakpoint from '../common/hooks/use-match-breakpoint';
import breakpoints from '../common/constants/breakpoints';
import generatePaymentUrl from '../utils/generate-payment-url';
import useSortedData from './hooks/use-sorted-data';
import isAnnualAvailable from './verifiers/is-annual-available';
import getPlanNames from './helpers/get-plan-names';

const {
  SubscriptionType: { DOMAIN_SUBSCRIPTION },
  SubscriptionTerm: { MONTHLY, ANNUAL },
  hostingPackageFeatureNames: features,
} = constants;

const {
  Paragraph,
  Heading,
  Container,
  Row,
  Col,
  SectionContent,
  SectionHeader,
  Section,
  CompactPlans,
  PaywallHeader,
  PaywallBody,
  PaywallFooter,
  ButtonsHeader,
  Box,
} = designSystem;

const {
  getDomainPromoCaptions,
  getAnnualDiscountCaptions,
  getCollapseCaptions,
  getFooterCaptions,
  getToggleData,
  getHighlightedPlanType,
} = helpers;

const defaultCaptions = {};
const defaultPaymentMethods = [];
const defaultDisabledPlans = [];
const defaultFeatureFLags = {};
const defaultProducts = [];
const defaultDiscount = {};

function MultipurposePaywall({
  captions = defaultCaptions,
  currency = 'USD',
  userId = '',
  discount = defaultDiscount,
  domainDiscount = null,
  defaultPaymentPeriod = PER_YEAR,
  onPeriodChange = Function.prototype,
  currentPlan,
  currentTerm = '',
  periodTogglerDisabled = false,
  periodTogglerHidden = false,
  minRequiredPlan = '',
  domainName = '',
  siteId = null,
  onSelect,
  appElementSelector = 'body',
  disabledPlans = defaultDisabledPlans,
  featureFlags = defaultFeatureFLags,
  onTooltipHover = Function.prototype,
  onScroll = Function.prototype,
  view = viewMode.DIALOG,
  onBackButtonClick = null,
  onClose = null,
  initiator = '',
  backUrl = '',
  successUrl = '',
  failUrl = '',
  closeUrl = '',
  moneyBackGuarantee = 0,
  paymentMethods = defaultPaymentMethods,
  partnerId,
  partnerName,
  availablePlans,
  isB2C,
  termsLink = null,
  products = defaultProducts,
  highlightedFeatures = null,
  isCtaVisible = true,
}) {
  const [prices, setPrices] = useState({});
  const [annualAvailable, setAnnualAvailable] = useState(false);
  const [paymentPeriod, setPaymentPeriod] = useState(defaultPaymentPeriod);
  const [isLoading, setIsLoading] = useState(true);
  const isLgBreakpoint = useMatchBreakpoint(breakpoints.LG);
  const yoproducts = yoproductsServiceClient.get(isB2C);
  const paymentPeriodName = paymentPeriod === PER_YEAR ? 'annual' : 'monthly';
  const FOOTER_CAPTIONS = getFooterCaptions({ moneyBackGuarantee, paymentMethods, partnerName });
  const COLLAPSE_CAPTIONS = getCollapseCaptions();

  const { value: discountValue, endDate: discountEndDate } = discount;
  const isExtraPricePresented = Boolean(discountValue);
  const areEcommercePlans = verifiers.isEcommercePlanType(currentPlan);
  const isCompactLayout = !isLgBreakpoint;

  useEffect(() => {
    const queryParams = {
      ...(isExtraPricePresented && { extra_prices: discountType.NO_DISCOUNT }),
      ...(domainDiscount && { discount: domainDiscount }),
    };

    yoproducts.requests.partnerProductsTree({ partnerId }, queryParams).then((data) => {
      setPrices(data);
      setAnnualAvailable(isAnnualAvailable(data));
      setIsLoading(false);
    });
  }, [userId, yoproducts.requests, isExtraPricePresented, isB2C, partnerId, domainDiscount]);

  const availablePlansData = useAvailablePlans(availablePlans, currentPlan);
  const isToggleHidden = periodTogglerHidden || !annualAvailable;

  const toggleCaptions = useMemo(() => {
    if (isToggleHidden || periodTogglerDisabled) {
      return null;
    }

    if (domainDiscount && availablePlansData.features.includes(features.FREE_DOMAIN_PROMO)) {
      return getDomainPromoCaptions({
        currency,
        prices,
        availablePlanIds: availablePlansData.planIds,
        domainName,
        domainDiscount,
      });
    }

    return getAnnualDiscountCaptions(currency, prices, availablePlansData.planIds);
  }, [
    availablePlansData,
    currency,
    prices,
    isToggleHidden,
    periodTogglerDisabled,
    domainName,
    domainDiscount,
  ]);

  const onPlanSelect = useCallback(
    (type) => {
      const term = API_TERM_VALUES[paymentPeriod];

      const paymentParams = {
        products: [...products, type, !!domainName && DOMAIN_SUBSCRIPTION].filter(Boolean),
        term,
        ...(domainName && { domain_name: domainName }),
        ...(siteId && { latitude_site_id: siteId }),
        ...(initiator && { initiator }),
        ...(backUrl && { back_url: backUrl }),
        ...(successUrl && { success_url: successUrl }),
        ...(failUrl && { fail_url: failUrl }),
        ...(closeUrl && { close_url: closeUrl }),
        ...(term === ANNUAL && currentTerm === MONTHLY && { p1y_upgrade: true }),
      };

      const paymentUrl = generatePaymentUrl(paymentParams, isB2C);

      onSelect(paymentUrl, type, paymentPeriodName);
    },
    [
      backUrl,
      closeUrl,
      domainName,
      failUrl,
      initiator,
      onSelect,
      paymentPeriod,
      paymentPeriodName,
      siteId,
      successUrl,
      isB2C,
      products,
      currentTerm,
    ]
  );

  const computedPlans = usePlansData({
    currentPlan,
    currentTerm,
    prices,
    currency,
    minRequiredPlan,
    disabledPlans,
    paymentPeriod,
    discountValue,
    availablePlansData,
    areEcommercePlans,
    onPlanSelect,
  });

  const handlePeriodChange = (value) => {
    setPaymentPeriod(value);
    onPeriodChange(currentPlan, value === PER_YEAR ? 'annual' : 'monthly');
  };

  const handleTooltipHover = (id) => {
    onTooltipHover(id, currentPlan, paymentPeriodName);
  };

  const handleScroll = (event) => {
    onScroll(event, currentPlan, paymentPeriodName);
  };

  const handleBackButton = () => {
    if (onBackButtonClick) {
      onBackButtonClick();
    } else {
      onClose();
    }
  };

  const toggleData = getToggleData(paymentPeriod, handlePeriodChange, periodTogglerDisabled);
  const highlightedPlan = getHighlightedPlanType(computedPlans);
  const planFeatures = usePlanFeatures({
    paymentPeriod,
    disabledPlans,
    availablePlansData,
    areEcommercePlans,
    featureFlags,
    isB2C,
    highlightedFeatures,
    domainDiscount,
  });

  const [activeCompactPlans, setCompactPlans] = useState(() => {
    const activePlan = computedPlans.find((plan) => plan.highlighted);
    const activePlans = [];

    if (activePlan) {
      activePlans.push(activePlan.type);
    }

    return activePlans;
  });

  const onCollapseClick = useCallback(
    (type) => {
      const compactPlans = [...activeCompactPlans];
      const index = compactPlans.indexOf(type);

      if (index !== -1) {
        compactPlans.splice(index, 1);
      } else {
        compactPlans.push(type);
      }

      setCompactPlans(compactPlans);
    },
    [activeCompactPlans]
  );

  const compactPlans = useCompactPlansData({
    computedPlans,
    planFeatures,
    activeCompactPlans,
    onCollapseClick,
  });

  const sortedData = useSortedData({
    computedPlans,
    compactPlans,
    planFeatures,
    areEcommercePlans,
    isB2C,
  });

  const getPaywallBody = () => {
    if (areEcommercePlans) {
      return (
        <PaywallBody
          data={sortedData.planFeatures}
          highlightedElementId={highlightedPlan}
          onTooltipHover={handleTooltipHover}
          isLoading={isLoading}
        />
      );
    }

    const planNames = getPlanNames(availablePlansData.plans);

    return (
      <>
        {sortedData.planFeatures.map(({ id, title, data }) =>
          id === planFeaturesGroupIds.CORE ? (
            <PaywallBody
              key={id}
              data={data}
              highlightedElementId={highlightedPlan}
              onTooltipHover={handleTooltipHover}
              isLoading={isLoading}
            />
          ) : (
            !!data.length && (
              <Box paddingTop="spacing-xl" key={id}>
                <PaywallBody
                  groupTitle={title}
                  availablePlans={planNames}
                  data={data}
                  highlightedElementId={highlightedPlan}
                  onTooltipHover={handleTooltipHover}
                  isLoading={isLoading}
                />
              </Box>
            )
          )
        )}
      </>
    );
  };

  const content = (
    <React.Fragment>
      {(onBackButtonClick || onClose) && (
        <ButtonsHeader
          isSticky={isCompactLayout}
          prevButton={{
            onClick: handleBackButton,
            iconGlyph: 'small-left-arrow',
            label: captions.backButtonLabel || t('Back to editor'),
          }}
        />
      )}
      <Container className="g-0">
        <Row className="justify-content-center g-0">
          <Col xs={11} sm={10} md={8} lg={12}>
            <Section>
              <SectionHeader>
                <Heading type="heading-1" align="center">
                  {captions.title ||
                    (areEcommercePlans
                      ? t('Upgrade your Online store')
                      : t('Upgrade your subscription'))}
                </Heading>

                {captions.description && (
                  <Paragraph size="large" align="center">
                    <strong>
                      {captions.description.first}
                      <br />
                      {captions.description.second}
                    </strong>
                  </Paragraph>
                )}
              </SectionHeader>
              <SectionContent>
                {isExtraPricePresented && (
                  <DiscountBanner
                    endDate={discountEndDate}
                    discount={discountValue}
                    termsLink={termsLink}
                  />
                )}
                <PaywallHeader
                  isLoading={isLoading}
                  withExtraPrice={isExtraPricePresented}
                  isCompact={isCompactLayout}
                  plans={sortedData.computedPlans}
                  isSticky
                  toggle={toggleData}
                  isHiddenToggle={isToggleHidden}
                  cta={toggleCaptions}
                  isCtaVisible={!toggleData.disabled && isCtaVisible}
                />
                {isCompactLayout ? (
                  <CompactPlans
                    isLoading={isLoading}
                    withExtraPrice={isExtraPricePresented}
                    plans={sortedData.compactPlans}
                    collapseOpenText={COLLAPSE_CAPTIONS.collapseOpenText}
                    collapseHiddenText={COLLAPSE_CAPTIONS.collapseHiddenText}
                  />
                ) : (
                  getPaywallBody()
                )}
                <PaywallFooter {...FOOTER_CAPTIONS} paymentMethods={paymentMethods} />
              </SectionContent>
            </Section>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );

  if (view === viewMode.CONTENT) {
    return content;
  }

  // Hide other page content from screen readers
  ReactModal.setAppElement(appElementSelector);

  return (
    <ReactModal
      isOpen
      className="ws-paywall-dialog"
      overlayClassName={{
        base: 'wp-dialog-overlay wp-dialog-overlay--without-text-styles',
        afterOpen: 'wp-dialog-overlay--after-open',
        beforeClose: 'wp-dialog-overlay--before-close',
      }}
    >
      <div
        className="ws-paywall-dialog__container"
        onScroll={!isCompactLayout ? handleScroll : Function.prototype}
      >
        {content}
      </div>
    </ReactModal>
  );
}

MultipurposePaywall.propTypes = {
  captions: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.shape({
      first: PropTypes.string,
      second: PropTypes.string,
    }),
    backButtonLabel: PropTypes.string,
  }),
  currentPlan: PropTypes.string.isRequired,
  currentTerm: PropTypes.string,
  currency: PropTypes.string,
  minRequiredPlan: PropTypes.string,
  domainName: PropTypes.string,
  discount: PropTypes.shape({
    value: PropTypes.number,
    endDate: PropTypes.number,
  }),
  domainDiscount: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  defaultPaymentPeriod: PropTypes.oneOf([PER_MONTH, PER_YEAR]),
  userId: PropTypes.string,
  siteId: PropTypes.string,
  appElementSelector: PropTypes.string,
  periodTogglerHidden: PropTypes.bool,
  periodTogglerDisabled: PropTypes.bool,
  view: PropTypes.oneOf([viewMode.DIALOG, viewMode.CONTENT]),
  disabledPlans: PropTypes.arrayOf(PropTypes.string),
  featureFlags: PropTypes.shape({}),
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onBackButtonClick: PropTypes.func,
  onScroll: PropTypes.func,
  onTooltipHover: PropTypes.func,
  onPeriodChange: PropTypes.func,
  initiator: PropTypes.string,
  backUrl: PropTypes.string,
  successUrl: PropTypes.string,
  failUrl: PropTypes.string,
  closeUrl: PropTypes.string,
  paymentMethods: PropTypes.arrayOf(PropTypes.string),
  moneyBackGuarantee: PropTypes.number,
  availablePlans: PropTypes.shape().isRequired,
  partnerId: PropTypes.string.isRequired,
  partnerName: PropTypes.string.isRequired,
  isB2C: PropTypes.bool.isRequired,
  termsLink: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.string),
  highlightedFeatures: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      position: PropTypes.number,
      highlightDisabledOnly: PropTypes.bool,
    })
  ),
  isCtaVisible: PropTypes.bool,
};

export default MultipurposePaywall;
